import type { Monitor } from '@zg-rentals/monitor-base';
import { appInfo } from '@zg-rentals/app-info';
import { getClient, setClient } from '@zg-rentals/http-client';
import { type LogErrorBeforeSend, setGlobalErrorContext } from '@zg-rentals/log-error';
import { setGlobalMonitor } from '@zg-rentals/monitor-base';
import type { Tracer } from '@zg-rentals/trace';
import { type Logger, setGlobalLogger } from '@zg-rentals/logger-base';

export type Plugin = (
  arg: { bootstrap: Bootstrap<Tracer>; appName: string; buildNumber: number },
  helpers: {
    loop: (
      callback: (controller: AbortController) => void | Promise<void>,
      interval: number,
      options?: { maxCorrectableDriftMs?: number },
    ) => Promise<void>;
  },
) => Promise<unknown> | unknown;

export function definePlugin(plugin: Plugin): Plugin {
  return plugin;
}

export abstract class BootstrapBuilder {
  protected logErrorBeforeSend?: LogErrorBeforeSend;

  constructor(
    public readonly appName: string,
    public readonly buildNumber: number,
  ) {}

  withLogErrorBeforeSend(logErrorBeforeSend: LogErrorBeforeSend): this {
    this.logErrorBeforeSend = logErrorBeforeSend;
    return this;
  }

  abstract build(): Bootstrap<Tracer>;
}

export class Bootstrap<T extends Tracer> {
  private readonly callbacks: Array<() => ReturnType<Plugin>> = [];

  constructor(
    protected readonly appName: string,
    protected readonly buildNumber: number,
    protected readonly plugins: Array<Plugin>,

    public readonly logger: Logger,
    public readonly monitor: Monitor,
    public readonly tracer: T,
    public readonly httpClient = getClient(),
  ) {
    appInfo.set({ name: appName, buildNumber });

    plugins.forEach((plugin) =>
      this.onRegister(() =>
        plugin(
          {
            appName: this.appName,
            bootstrap: this,
            buildNumber: this.buildNumber,
          },
          {
            // Ticks are scheduled against a fixed `nextTick` grid rather than a flat
            // `interval` after each callback, so that ordinary drift (e.g. a slow tick)
            // is corrected on the following tick instead of compounding over time. Drift
            // beyond `maxCorrectableDriftMs` is left uncorrected for that tick and the
            // grid resets from now, so a stalled/overloaded loop doesn't fire back-to-back
            // trying to make up lost time.
            loop: async (callback, interval, { maxCorrectableDriftMs = interval * 0.2 } = {}) => {
              const controller = new AbortController();
              let nextTick = Date.now() + interval;

              while (!controller.signal.aborted) {
                try {
                  await Promise.resolve(callback(controller));
                } catch (error) {
                  this.logger.error(error);
                }

                const now = Date.now();
                const drift = now - nextTick;
                const isFarBehind = drift > maxCorrectableDriftMs;

                // isFarBehind: give up on the grid, wait a full interval from now.
                // drift > 0 (late, correctable): skip the missed slot, land on nextTick + interval.
                // drift <= 0 (early or on time): wait for nextTick itself, don't skip ahead.
                const delay = isFarBehind ? interval : Math.max(0, drift > 0 ? interval - drift : -drift);
                nextTick = isFarBehind ? now + interval : nextTick + interval;

                await new Promise((resolve) => setTimeout(resolve, delay));
              }
            },
          },
        ),
      ),
    );

    setGlobalLogger(logger);
    setGlobalMonitor(monitor);

    setGlobalErrorContext({ appName, buildNumber });

    setClient(httpClient);

    this.onRegister(() => {
      // Note: do not wait for monitor count to flush when registering, i.e. no async/await
      monitor.count({ name: 'bootstrap.register' });
      monitor.count({ name: `buildNumber ${this.buildNumber}` });
    });
  }

  /**
   * Schedule a callback to execute when the bootstrap instance is registered.
   * Useful to recording a startup metric, for example.
   */
  protected onRegister(callback: (typeof this.callbacks)[number]) {
    this.callbacks.push(callback);
  }

  /**
   * Call register _once per runtime_ in your application. For a client-only app
   * this is likey wherever you call ReactDOM.createRoot. For a server app this
   * should be called in your docker-entrypoint. For SSR apps, it should be called
   * in each place.
   */
  public async register() {
    await Promise.all(this.callbacks.splice(0).map((callback) => Promise.resolve(callback())));
  }
}
